import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { Subject, take, takeUntil } from 'rxjs';

import { IPaperChecklistData } from 'editing-tool/src/app/shared/components/paper/steps/checklist/paper-checklist-data';
import { NotificationDialogService } from 'editing-tool/src/app/shared/notification/notification-dialog-service';
import { ValidationService } from 'sc-common';
import { ApiChecklistItem } from 'sc-common/core/services/open-api/main-api-client';

type CheckboxItem = { checkbox: UntypedFormControl; item: ApiChecklistItem; };

@Component({
    selector: 'et-paper-checklist',
    templateUrl: './paper-checklist.component.html',
    standalone: false
})
export class PaperChecklistComponent implements OnInit, OnDestroy {

    @Input()
    public data: IPaperChecklistData;

    @Input()
    public isReadonly: boolean;

    public form: UntypedFormGroup;

    public checkboxItems: CheckboxItem[] = [];

    public createdDateTime: Date;

    public canBeSubmitted = false;

    public requestInProcess = false;

    public showWarning = false;

    private readonly _destroy$ = new Subject<void>();

    constructor(
        private readonly _router: Router,
        private readonly _notificationDialogService: NotificationDialogService) {
    }

    public ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    public ngOnInit(): void {

        this.data.checklist$
            .pipe(takeUntil(this._destroy$))
            .subscribe(apiModel => {

                this.canBeSubmitted = apiModel.canBeSubmitted;
                this.createdDateTime = apiModel.createdDateTime;
                this.showWarning = !apiModel.canBeSubmitted && apiModel.createdDateTime === null;

                this._buildForm(apiModel.items);
            });
    }

    public submitChecklist(): void {

        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        this.requestInProcess = true;

        this.checkboxItems.forEach(checkboxItem => {
            checkboxItem.item.checked = checkboxItem.checkbox.value;
        });

        const items = this.checkboxItems.map(checkboxItem => checkboxItem.item);

        this.data.create(items)
            .pipe(take(1), takeUntil(this._destroy$))
            .subscribe(() => {
                this._navigatePaperList();
                this.requestInProcess = false;
            });
    }

    private _buildForm(items: ApiChecklistItem[]): void {

        const checkboxesFormArray = new UntypedFormArray([]);

        this.form = new UntypedFormGroup({ checkboxes: checkboxesFormArray },
            ValidationService.canFormBeSubmitted(
                () => this.canBeSubmitted,
                { key: 'readOnly', value: $localize`The form is readonly.` }));

        items.map(item =>
        ({
            checkbox: new UntypedFormControl(
                { value: item.checked, disabled: !this.canBeSubmitted || this.isReadonly },
                item.templateItem.isRequired ? Validators.requiredTrue : null),
            item: item
        })
        ).forEach(ci => this.checkboxItems.push(ci));

        this.checkboxItems.forEach(ci => checkboxesFormArray.push(ci.checkbox));
    }

    private _navigatePaperList(): void {

        this._router.navigate(['author', 'papers'])
            .then(success => {
                if (success) {
                    this._notificationDialogService.showMessage({
                        header: $localize`Your paper is submitted `,
                        message: $localize`Please note this information is in read only status. You can add comments only.`
                    });
                }
            });
    }
}
